import { useCallback } from 'react';
import { Box, Button, Icon, Paragraph } from '@zillow/constellation-10';
import { IconChevronUpFilled } from '@zillow/constellation-icons';
import { AvatarGroup, FULLSTORY_EXCLUDE_CLASS_NAME, UnreadIndicator } from '@zg-rentals/satellite-components';
import { useCachedLeadByConversationId } from '../../../../hooks/useLeadManagementTableQuery';
import getRenterDisplayName from '../../../../utils/getRenterDisplayName';
import { minimizedMessagingPillStyles } from '../styles';

type MinimizedMessagingPillProps = {
  conversationId: string;
  rightPosition: number;
  onRestore: () => void;
};

/**
 * The minimized form of the messaging window: a floating pill with the renter's avatar, name, and
 * unread state (fed by the lead-table cache, which realtime invalidation keeps fresh while the
 * message thread itself is unmounted). Clicking restores the full window.
 */
export default function MinimizedMessagingPill({
  conversationId,
  rightPosition,
  onRestore,
}: MinimizedMessagingPillProps) {
  const lead = useCachedLeadByConversationId(conversationId);
  const renterName = lead?.renterInfo?.renterName;
  const displayName = getRenterDisplayName(renterName, lead?.latestContact?.isPhoneLead);
  const hasUnread = lead?.latestContact?.isRead === false;

  // The pill only ever mounts because the user just minimized the window, so taking focus here
  // keeps the keyboard position from falling back to <body> when that button unmounts.
  const focusOnMount = useCallback((node: HTMLButtonElement | null) => node?.focus(), []);

  return (
    <Button
      ref={focusOnMount}
      aria-label={`Restore chat with ${displayName}`}
      // On the Button itself (not just the name) so the PII-bearing aria-label is excluded too.
      className={FULLSTORY_EXCLUDE_CLASS_NAME}
      emphasis="bare"
      style={{ right: `${rightPosition}px`, zIndex: 1000 }}
      css={minimizedMessagingPillStyles}
      onClick={onRestore}
    >
      {/* Button wraps its children in a block label, so the row layout needs its own flex container. */}
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
        {/* The raw name, not displayName — "(Phone lead)" must not become avatar initials. */}
        <AvatarGroup participants={[renterName || '']} variant="default" singleParticipantSize="sm" />
        <Paragraph textStyle="body-bold" css={{ truncate: true, maxWidth: '4000' }}>
          {displayName}
        </Paragraph>
        {hasUnread && <UnreadIndicator />}
        <Icon render={<IconChevronUpFilled />} css={{ color: 'text.subtle' }} />
      </Box>
    </Button>
  );
}
